import { useRecoilValue } from 'recoil'
import healthClaimFormAtom from '@/recoil/healthClaimForm/atom'
import userAtom from '@/recoil/user/atom'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { BACKOFFICE_USER_PERMISSION } from '@/constant/BACKOFFICE_PERMISSION'
import { ClaimStatusCodeEnum } from '../../HealthClaimPage/ClaimHandlingTable/types'
import { useListHealthClaimUserApprove } from '@/services/healthClaim/useListHealthClaimUserApprove'
import ReclaimByThisClaimButton from '../ReclaimByThisClaimButton/ReclaimByThisClaimButton'
import DeleteClaimButton from '../DeleteClaimButton/DeleteClaimButton'
import EditClaimButton from '../EditClaimButton/EditClaimButton'
import ViewRejectDocumentButton from '../ViewRejectDocumentButton/ViewRejectDocumentButton'
import RejectClaimButton from '../RejectClaimButton/RejectClaimButton'
import ApproveButton from '../ApproveButton/ApproveButton'
import SendApproveButton from '../SendApproveButton/SendApproveButton'
import { AcceptClaimButton } from '../AcceptClaimButton/AcceptClaimButton'
import { EmotionJSX } from '@emotion/react/types/jsx-namespace'
import SendPvButton from '../SendPvButton/SendPvButton'
import { useParams } from 'react-router-dom'
import { hasAllRequiredPermissions } from '@/libs/validatePermission'
import { ListHealthClaimUserApprove } from '@/services/healthClaim/useListHealthClaimUserApprove/types'
import { ButtonId } from './types'

const ClaimActionBarHook = () => {
  const { claimFormId } = useParams<{ claimFormId?: string }>()
  const user = useRecoilValue(userAtom)
  const healthClaim = useRecoilValue(healthClaimFormAtom)
  const backofficeFullname = `${user.firstname} ${user.lastname}`
  const queryApprover = useListHealthClaimUserApprove({
    search: backofficeFullname,
    page: 1,
    limit: 1,
    enabled: !!backofficeFullname,
  })

  // Derived values
  const ownerId = healthClaim.data?.ownerId || ''
  const currentStatus = healthClaim.data?.statusCode as ClaimStatusCodeEnum
  const isCreateOrEditMode =
    healthClaim.mode === HealthClaimModeEnum.CREATE || healthClaim.mode === HealthClaimModeEnum.EDIT
  const isOwner = ownerId === user.id

  // Permission checks
  const isManager = user.permissions.includes(BACKOFFICE_USER_PERMISSION.APP010104)
  const isStaff = user.permissions.includes(BACKOFFICE_USER_PERMISSION.CLM010103)

  // Action enablement logic
  const canOwnerManage = isOwner // The owner can always manage their case (except for limited statuses)
  const canManagerApprove =
    isManager &&
    [ClaimStatusCodeEnum.PENDING_APPROVAL, ClaimStatusCodeEnum.APPROVED].includes(currentStatus) // The manager can approve after pending approval

  const enableActionButton = canOwnerManage || canManagerApprove

  const managerPermissions = [
    BACKOFFICE_USER_PERMISSION.APP010104,
    BACKOFFICE_USER_PERMISSION.CLM010103,
    BACKOFFICE_USER_PERMISSION.CLM010105,
  ]

  const staffPermissions = [
    BACKOFFICE_USER_PERMISSION.CLM010103,
    BACKOFFICE_USER_PERMISSION.CLM010105,
  ]

  // Catalog of all possible buttons mapped by stable IDs
  const buttonsCatalog: Record<ButtonId, EmotionJSX.Element> = {
    'accept-claim': <AcceptClaimButton />,
    reclaim: <ReclaimByThisClaimButton />,
    delete: <DeleteClaimButton />,
    edit: <EditClaimButton />,
    reject: <RejectClaimButton />,
    approve: <ApproveButton />,
    'send-approve': <SendApproveButton />,
    'send-pv': <SendPvButton />,
    'view-reject-doc': (
      <ViewRejectDocumentButton
        claimFormId={claimFormId}
        hasRejectDocument={healthClaim.data?.hasRejectDocument}
      />
    ),
  }

  const decisionTree: Array<{
    permissions: Array<string>
    status: ClaimStatusCodeEnum
    buttonIds: Array<{
      buttonId: ButtonId
      isOwnerOnly: boolean
    }>
  }> = [
    // High permission entries first - users with special permissions get enhanced functionality
    {
      permissions: managerPermissions,
      status: ClaimStatusCodeEnum.SUBMITTED,
      buttonIds: [{ buttonId: 'accept-claim', isOwnerOnly: false }],
    },
    {
      permissions: managerPermissions,
      status: ClaimStatusCodeEnum.IN_REVIEW,
      buttonIds: [
        { buttonId: 'reclaim', isOwnerOnly: false },
        { buttonId: 'delete', isOwnerOnly: true },
        { buttonId: 'edit', isOwnerOnly: true },
        { buttonId: 'reject', isOwnerOnly: true },
        { buttonId: 'send-approve', isOwnerOnly: true },
        { buttonId: 'send-pv', isOwnerOnly: true },
      ],
    },
    {
      permissions: managerPermissions,
      status: ClaimStatusCodeEnum.DOCUMENTS_REQUESTED,
      buttonIds: [
        { buttonId: 'reclaim', isOwnerOnly: false },
        { buttonId: 'delete', isOwnerOnly: true },
        { buttonId: 'edit', isOwnerOnly: true },
        { buttonId: 'reject', isOwnerOnly: true },
        { buttonId: 'send-approve', isOwnerOnly: true },
        { buttonId: 'send-pv', isOwnerOnly: true },
      ],
    },
    {
      permissions: managerPermissions,
      status: ClaimStatusCodeEnum.REJECTED,
      buttonIds: [
        { buttonId: 'reclaim', isOwnerOnly: false },
        { buttonId: 'view-reject-doc', isOwnerOnly: false },
      ],
    },
    {
      permissions: managerPermissions,
      status: ClaimStatusCodeEnum.REJECTED_ON_PV,
      buttonIds: [
        { buttonId: 'reclaim', isOwnerOnly: false },
        { buttonId: 'view-reject-doc', isOwnerOnly: false },
      ],
    },
    {
      permissions: managerPermissions,
      status: ClaimStatusCodeEnum.PENDING_APPROVAL,
      buttonIds: [
        { buttonId: 'reclaim', isOwnerOnly: false },
        { buttonId: 'edit', isOwnerOnly: false },
        { buttonId: 'reject', isOwnerOnly: false },
        { buttonId: 'approve', isOwnerOnly: false },
        { buttonId: 'send-pv', isOwnerOnly: false },
      ],
    },
    {
      permissions: managerPermissions,
      status: ClaimStatusCodeEnum.APPROVED,
      buttonIds: [{ buttonId: 'reclaim', isOwnerOnly: false }],
    },
    {
      permissions: managerPermissions,
      status: ClaimStatusCodeEnum.PENDING_PAYMENT,
      buttonIds: [{ buttonId: 'reclaim', isOwnerOnly: false }],
    },
    {
      permissions: managerPermissions,
      status: ClaimStatusCodeEnum.COMPLETED,
      buttonIds: [{ buttonId: 'reclaim', isOwnerOnly: false }],
    },
    {
      permissions: managerPermissions,
      status: ClaimStatusCodeEnum.PENDING_DOCUMENT,
      buttonIds: [{ buttonId: 'reclaim', isOwnerOnly: false }],
    },
    // Default entries for users without special permissions
    {
      permissions: staffPermissions,
      status: ClaimStatusCodeEnum.SUBMITTED,
      buttonIds: [{ buttonId: 'accept-claim', isOwnerOnly: false }],
    },
    {
      permissions: staffPermissions,
      status: ClaimStatusCodeEnum.IN_REVIEW,
      buttonIds: [
        { buttonId: 'reclaim', isOwnerOnly: false },
        { buttonId: 'delete', isOwnerOnly: true },
        { buttonId: 'edit', isOwnerOnly: true },
        { buttonId: 'reject', isOwnerOnly: true },
        { buttonId: 'send-approve', isOwnerOnly: true },
        { buttonId: 'send-pv', isOwnerOnly: true },
      ],
    },
    {
      permissions: staffPermissions,
      status: ClaimStatusCodeEnum.DOCUMENTS_REQUESTED,
      buttonIds: [
        { buttonId: 'reclaim', isOwnerOnly: false },
        { buttonId: 'delete', isOwnerOnly: true },
        { buttonId: 'edit', isOwnerOnly: true },
        { buttonId: 'reject', isOwnerOnly: true },
        { buttonId: 'send-approve', isOwnerOnly: true },
        { buttonId: 'send-pv', isOwnerOnly: true },
      ],
    },
    {
      permissions: staffPermissions,
      status: ClaimStatusCodeEnum.REJECTED,
      buttonIds: [
        { buttonId: 'reclaim', isOwnerOnly: false },
        { buttonId: 'view-reject-doc', isOwnerOnly: false },
      ],
    },
    {
      permissions: staffPermissions,
      status: ClaimStatusCodeEnum.REJECTED_ON_PV,
      buttonIds: [
        { buttonId: 'reclaim', isOwnerOnly: false },
        { buttonId: 'view-reject-doc', isOwnerOnly: false },
      ],
    },
    {
      permissions: staffPermissions,
      status: ClaimStatusCodeEnum.PENDING_APPROVAL,
      buttonIds: [{ buttonId: 'reclaim', isOwnerOnly: false }],
    },
    {
      permissions: staffPermissions,
      status: ClaimStatusCodeEnum.APPROVED,
      buttonIds: [{ buttonId: 'reclaim', isOwnerOnly: false }],
    },
    {
      permissions: staffPermissions,
      status: ClaimStatusCodeEnum.PENDING_PAYMENT,
      buttonIds: [{ buttonId: 'reclaim', isOwnerOnly: false }],
    },
    {
      permissions: staffPermissions,
      status: ClaimStatusCodeEnum.COMPLETED,
      buttonIds: [{ buttonId: 'reclaim', isOwnerOnly: false }],
    },
    {
      permissions: staffPermissions,
      status: ClaimStatusCodeEnum.PENDING_DOCUMENT,
      buttonIds: [{ buttonId: 'reclaim', isOwnerOnly: false }],
    },
  ]

  // Function to get available buttons based on current user, claim status, and ownership
  const getAvailableButtons = () => {
    const matched =
      decisionTree
        .find(
          (entry) =>
            hasAllRequiredPermissions([...entry.permissions], user.permissions) &&
            entry.status === healthClaim.data?.statusCode,
        )
        ?.buttonIds.filter((b) => (isOwner ? true : !b.isOwnerOnly))
        .map((bi) => bi.buttonId) || ([] as ButtonId[])

    if (healthClaim.data?.statusCode === ClaimStatusCodeEnum.PENDING_APPROVAL) {
      const defaultButtons = [buttonsCatalog.reclaim]

      // always show reclaim button to the owner when status is pending approval
      if (isOwner) {
        return defaultButtons
      }

      // If not the owner, check if the current user can approve
      // get approver by fullname
      const approver = queryApprover.data?.data[0]

      if (!approver) {
        return defaultButtons
      }

      const canApprove = isHealthClaimApprover(approver, healthClaim.data?.approvedClaimAmount)

      if (isManager && canApprove) {
        return [
          buttonsCatalog.reclaim,
          buttonsCatalog.edit,
          buttonsCatalog.reject,
          buttonsCatalog.approve,
          buttonsCatalog['send-pv'],
        ]
      }

      return defaultButtons
    }

    // Return buttons based on matched IDs
    if (matched.length > 0) {
      return matched.map((id) => buttonsCatalog[id])
    }

    // No buttons available
    return []
  }

  const isHealthClaimApprover = (
    approver: ListHealthClaimUserApprove,
    approvedClaimAmount: number,
  ) => {
    if (!approver) {
      return false
    }

    // check the user can approve by approvedClaimAmount
    const amountTokens = approver.approvalAmountDisplay?.split(' - ') // approvalAmountDisplay e.g. "1,000 - 5,000 บาท"

    if (approvedClaimAmount !== undefined && amountTokens?.length === 2) {
      const [minStr, maxStr] = amountTokens.map((s) => s.replace(/[^0-9]/g, ''))
      const min = Number.parseInt(minStr, 10)
      const max = Number.parseInt(maxStr, 10)

      if (
        !Number.isNaN(min) &&
        !Number.isNaN(max) &&
        approvedClaimAmount >= min &&
        approvedClaimAmount <= max
      ) {
        return true
      }
    }

    return false
  }

  return {
    user,
    ownerId,
    healthClaim,
    isCreateOrEditMode,
    isOwner,
    isManager,
    isStaff,
    canOwnerManage,
    canManagerApprove,
    enableActionButton,
    managerPermissions,
    staffPermissions,
    getAvailableButtons,
    isHealthClaimApprover,
  }
}

export default ClaimActionBarHook
